import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, inject } from '@angular/core';
import { cardsConfig } from '../const/cardsConfig';
import {
  BehaviorSubject,
  forkJoin,
  interval,
  switchMap,
  tap,
} from 'rxjs';
import { Environment } from '@t-llevo/util';
import { apiRoutes } from '../api-routing';
import { UserService } from '../../api/services/user.service';
import { TravelService } from '../../api/services/travel.service';
import { VersionResponse } from '../../api/models/responses/VersionResponse';
import { ConnectedResponse } from '../../api/models/responses/ConnectedResponse';
import { VersionControlService } from '../../api/services/version-control.service';

@Injectable()
export class SummaryService {
  private connectedDriversSubject = new BehaviorSubject<number | null>(null);
  connectedDrivers$ = this.connectedDriversSubject.asObservable();

  private connectedClientsSubject = new BehaviorSubject<number | null>(null);
  connectedClients$ = this.connectedClientsSubject.asObservable();

  private totalDriversSubject = new BehaviorSubject<number | null>(null);
  totalDrivers$ = this.totalDriversSubject.asObservable();

  private totalTravelsSubject = new BehaviorSubject<number | null>(null);
  totalTravels$ = this.totalTravelsSubject.asObservable();

  private driverAppLastVersionSubject = new BehaviorSubject<VersionResponse | null>(null);
  driverAppLastVersion$ = this.driverAppLastVersionSubject.asObservable();

  private clientAppLastVersionSubject = new BehaviorSubject<VersionResponse | null>(null);
  clientAppLastVersion$ = this.clientAppLastVersionSubject.asObservable();

  private userSrv = inject(UserService);
  private travelSrv = inject(TravelService)
  private versionSrv = inject(VersionControlService)

  constructor(
    private _http: HttpClient,
    @Inject(Environment) private env: Environment
  ) {}

  getCardsConfig() {
    return cardsConfig;
  }

  getVersionsData(){
    return forkJoin({
      driverApp: this.versionSrv.getLastVersionByPackageName('com.edtsoft.t_llevodriver'),
      clientApp: this.versionSrv.getLastVersionByPackageName('com.edtsoft.t_llevoclient')
    })
    .pipe(
      tap(data => {
        this.driverAppLastVersionSubject.next(data.driverApp)
        this.clientAppLastVersionSubject.next(data.clientApp)
      })
    )
  }

  getCardsData() {
    return forkJoin({
      connected: this._http.get<ConnectedResponse>(
        this.env.apiUrl + apiRoutes.routeConnected
      ),
      totalDrivers: this.userSrv.getUsers(),
      totalTravels: this.travelSrv.getTravels()
    }).pipe(
      tap((data) => {
        const totalDrivers = data.totalDrivers.results.filter((user) =>
          user.roles.includes('driver')
        ).length;

        this.connectedClientsSubject.next(data.connected.clients);
        this.connectedDriversSubject.next(data.connected.drivers);
        this.totalDriversSubject.next(totalDrivers);
        this.totalTravelsSubject.next(data.totalTravels.count)
      })
    );
  }

  getValueById(id: string) {
    switch (id) {
      case 'connectedDrivers':
        return this.connectedDrivers$;
      case 'connectedClients':
        return this.connectedClients$;
      case 'totalDrivers':
        return this.totalDrivers$
      case 'totalTravels':
        return this.totalTravels$
      default:
        throw new Error(`'${id}' no es un valor de id valido`);
    }
  }

  updateInterval(intervalRate: number) {
    return interval(intervalRate).pipe(switchMap(() => this.getCardsData()));
  }
}
